import React from 'react';
import {
  HStack,
  CheckCircleIcon,
  CloseCircleIcon,
  CloseCircleRedIcon,
  InfoIcon,
  LockIcon,
  Badge,
  Text,
} from '@packages/systems/spring';
import {ShimmerText} from '../../../animations/shimmer/shimmer-text';
import {StatusRowSpinnerIcon} from './StatusRowSpinnerIcon';

const statusIconMap = {
  executing: <StatusRowSpinnerIcon aria-label="Tool execution in progress" />,
  failed: <CloseCircleRedIcon colorVariant="danger" />,
  cancelled: <CloseCircleIcon />,
  partial: <CheckCircleIcon colorVariant="success" />,
  executed: <CheckCircleIcon colorVariant="success" />,
  /** Shown when the staleness guard blocks a tool call. */
  blocked: <InfoIcon />,
  /** Shown when the permission guard blocks a tool call. */
  permission_blocked: <LockIcon />,
};

interface StatusMessageDisplayProps extends React.PropsWithChildren {
  count?: number;
  status: keyof typeof statusIconMap;
  sufix?: React.ReactNode;
}

/**
 * Parses a string for backtick-delimited segments and returns React nodes
 * with inline code styling applied to backtick-wrapped content.
 */
function parseInlineCode(text: string): React.ReactNode {
  const parts = text.split(/`([^`]+)`/);
  if (parts.length === 1) return text;
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <Text
        key={index}
        font="code"
        padding="$0.25"
        borderRadius="$2"
        backgroundColor="$background3"
      >
        {part}
      </Text>
    ) : (
      part || null
    )
  );
}

/**
 * ## StatusMessageDisplay
 * Displays status message with icon for given status.
 */
export const StatusMessageDisplay = ({
  count,
  status,
  children,
  sufix = null,
}: StatusMessageDisplayProps) => {
  const statusIcon = (statusIconMap[status] as React.ReactNode) ?? null;
  const renderedChildren =
    typeof children === 'string' ? parseInlineCode(children) : children;
  return (
    <HStack justify="space-between" align="start">
      <HStack align="start">
        {statusIcon}
        <ShimmerText isActive={status === 'executing'} colorVariant="primary">
          {renderedChildren}
        </ShimmerText>
      </HStack>
      {count && count > 1 && <Badge density="compact">{`× ${count}`}</Badge>}
      {sufix}
    </HStack>
  );
};
